import { canonicalHash } from './canonical.js';
import { heuristic, isGoalState } from './heuristic.js';
import { applySlideByCoord, enumerateSlides } from './moves.js';
const FOUND = -1;
class BudgetError extends Error {
    reason;
    constructor(reason) {
        super(`solver budget: ${reason}`);
        this.reason = reason;
    }
}
export function solve(board, objective, opts = {}) {
    const start = Date.now();
    const maxNodes = opts.maxNodes ?? 5_000_000;
    const maxMs = opts.maxMs ?? 30_000;
    const maxDepth = opts.maxDepth ?? 40;
    const maxTT = opts.maxTTEntries ?? 2_000_000;
    let nodes = 0;
    if (isGoalState(board, objective)) {
        return { solution: [], depth: 0, nodesVisited: 0, computeMs: 0, truncated: null };
    }
    const rootH = heuristic(board, objective);
    if (!Number.isFinite(rootH)) {
        // Heuristic already proved the board unsolvable (e.g. stranded color).
        return { solution: null, depth: null, nodesVisited: 0, computeMs: Date.now() - start, truncated: null };
    }
    const budgetCheck = () => {
        if (nodes > maxNodes)
            throw new BudgetError('nodes');
        if (Date.now() - start > maxMs)
            throw new BudgetError('time');
    };
    const path = [];
    // Transposition table: canonical hash → lower bound on cost-to-go (in moves).
    // Persistent across IDA* iterations.
    const tt = new Map();
    const ttGet = (hash) => tt.get(hash) ?? 0;
    const ttSet = (hash, bound) => {
        if (bound <= 0)
            return;
        const existing = tt.get(hash);
        if (existing !== undefined && existing >= bound)
            return;
        if (existing === undefined && tt.size >= maxTT) {
            // Evict oldest (insertion-order map).
            const firstKey = tt.keys().next().value;
            if (firstKey !== undefined)
                tt.delete(firstKey);
        }
        tt.set(hash, bound);
    };
    // One DFS iteration bounded by f ≤ bound. Returns FOUND when goal hit,
    // or the smallest f-overshoot (the next bound to try) otherwise.
    //
    // onPath: set of canonical hashes on the current DFS path. Used to prune
    // successors whose canonical form is already on the path (cycle kill).
    // Sound for cost-to-go bounding because any optimal path avoids revisiting
    // ancestors (a revisit is a strict cycle and adds cost).
    function dfs(cur, curHash, g, bound, onPath) {
        nodes++;
        if ((nodes & 1023) === 0)
            budgetCheck();
        const h = heuristic(cur, objective);
        if (!Number.isFinite(h))
            return Infinity; // stranded color, etc.
        // Use TT to tighten h: cached bound is a proven lower bound on cost-to-go.
        const ttBound = ttGet(curHash);
        const effectiveH = h > ttBound ? h : ttBound;
        const f = g + effectiveH;
        if (f > bound)
            return f;
        if (isGoalState(cur, objective))
            return FOUND;
        if (g >= maxDepth)
            return Infinity;
        const moves = enumerateSlides(cur);
        if (moves.length === 0)
            return Infinity;
        // Move ordering: expand the child with the smallest f first to find
        // good bounds quickly. Precompute successor boards and hashes; drop any
        // successor already on the current path. Consult TT for each child too
        // so dead branches get sorted to the back (or pruned outright).
        const successors = [];
        for (const m of moves) {
            const { board: next, movesUsed } = applySlideByCoord(cur, m.from, m.direction);
            if (movesUsed === 0)
                continue;
            const hash = canonicalHash(next);
            if (onPath.has(hash))
                continue;
            const childH = heuristic(next, objective);
            if (!Number.isFinite(childH))
                continue; // dead end — skip
            const childTT = ttGet(hash);
            const childEffH = childH > childTT ? childH : childTT;
            successors.push({ step: m, next, hash, childF: g + 1 + childEffH });
        }
        successors.sort((a, b) => a.childF - b.childF);
        let min = Infinity;
        for (const { step, next, hash, childF } of successors) {
            if (childF > bound) {
                if (childF < min)
                    min = childF;
                continue;
            }
            onPath.add(hash);
            path.push(step);
            const t = dfs(next, hash, g + 1, bound, onPath);
            if (t === FOUND)
                return FOUND;
            if (t < min)
                min = t;
            path.pop();
            onPath.delete(hash);
        }
        // Record what we learned: min is a lower bound on (g + cost_to_go(cur))
        // achievable from here within this iteration's pruning. If min === Infinity
        // the state is either fully dead-end or we hit maxDepth; cache a large bound
        // so we don't re-enter it. Otherwise cache min - g (the cost-to-go lower
        // bound from cur).
        if (min === Infinity) {
            ttSet(curHash, maxDepth + 1);
        }
        else {
            ttSet(curHash, min - g);
        }
        return min;
    }
    try {
        let bound = rootH;
        const rootHash = canonicalHash(board);
        while (true) {
            const onPath = new Set([rootHash]);
            const t = dfs(board, rootHash, 0, bound, onPath);
            if (t === FOUND) {
                return {
                    solution: [...path],
                    depth: path.length,
                    nodesVisited: nodes,
                    computeMs: Date.now() - start,
                    truncated: null,
                };
            }
            if (t === Infinity || t > maxDepth) {
                return { solution: null, depth: null, nodesVisited: nodes, computeMs: Date.now() - start, truncated: null };
            }
            bound = t;
        }
    }
    catch (e) {
        if (e instanceof BudgetError) {
            return { solution: null, depth: null, nodesVisited: nodes, computeMs: Date.now() - start, truncated: e.reason };
        }
        throw e;
    }
}
